import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ExternalLink, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders, getApiErrorMessage, moneyValue } from "@/lib/streamcareShared";

const emptyForm = { amount: "", currency: "EUR", url: "", active: true };

const isPaypalHttpsUrl = (url) => {
  try {
    const parsed = new URL(String(url || "").trim());
    return parsed.protocol === "https:" && (parsed.hostname === "paypal.com" || parsed.hostname.endsWith(".paypal.com"));
  } catch (error) {
    return false;
  }
};

export function PaypalSettingsSection() {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [unmatchedSummary, setUnmatchedSummary] = useState({ total: 0, amounts: [] });
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [linksResponse, unmatchedResponse] = await Promise.all([
        axios.get(`${API}/settings/paypal-links`, { headers: authHeaders() }),
        axios.get(`${API}/settings/paypal-links/unmatched-amounts`, { headers: authHeaders() }),
      ]);
      setLinks(linksResponse.data || []);
      setUnmatchedSummary(unmatchedResponse.data || { total: 0, amounts: [] });
      const settingsResponse = await axios.get(`${API}/settings`, { headers: authHeaders() });
      const paypalStatus = (settingsResponse.data || []).find((item) => item.key === "paypal_payments_enabled");
      setPaymentsEnabled(String(paypalStatus?.value || "OFF").toUpperCase() === "ON");
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, "Chargement PayPal impossible");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const activeByAmount = useMemo(() => links.reduce((acc, link) => {
    if (link.active) acc[`${Number(link.amount).toFixed(2)} ${link.currency}`] = link.id;
    return acc;
  }, {}), [links]);

  const unmatchedLabel = useMemo(() => (unmatchedSummary.amounts || []).map((item) => moneyValue(item.amount, item.currency)).join(", "), [unmatchedSummary]);

  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = { amount: Number(form.amount), currency: form.currency.trim().toUpperCase(), url: form.url.trim(), active: Boolean(form.active) };
    if (!payload.amount || payload.amount <= 0) {
      toast.error("Saisissez un montant supérieur à 0.");
      return;
    }
    if (!isPaypalHttpsUrl(payload.url)) {
      toast.error("L’URL doit être une URL https:// PayPal valide.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await axios.patch(`${API}/settings/paypal-links/${editingId}`, payload, { headers: authHeaders() });
        toast.success("Lien PayPal mis à jour");
      } else {
        await axios.post(`${API}/settings/paypal-links`, payload, { headers: authHeaders() });
        toast.success("Lien PayPal ajouté");
      }
      reset();
      await loadLinks();
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, "Enregistrement PayPal impossible");
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const editLink = (link) => {
    setEditingId(link.id);
    setForm({ amount: String(link.amount), currency: link.currency || "EUR", url: link.url || "", active: Boolean(link.active) });
    setError("");
  };

  const toggleLink = async (link) => {
    try {
      await axios.patch(`${API}/settings/paypal-links/${link.id}`, { active: !link.active }, { headers: authHeaders() });
      toast.success(!link.active ? "Lien PayPal activé" : "Lien PayPal désactivé");
      await loadLinks();
    } catch (requestError) {
      toast.error(getApiErrorMessage(requestError, "Mise à jour PayPal impossible"));
    }
  };

  const deleteLink = async (link) => {
    if (!window.confirm(`Supprimer le lien PayPal ${moneyValue(link.amount, link.currency)} ?`)) return;
    try {
      await axios.delete(`${API}/settings/paypal-links/${link.id}`, { headers: authHeaders() });
      toast.success("Lien PayPal supprimé");
      if (editingId === link.id) reset();
      await loadLinks();
    } catch (requestError) {
      toast.error(getApiErrorMessage(requestError, "Suppression PayPal impossible"));
    }
  };

  const togglePaymentsEnabled = async () => {
    try {
      await axios.patch(`${API}/settings/paypal_payments_enabled`, { value: paymentsEnabled ? "OFF" : "ON" }, { headers: authHeaders() });
      toast.success(paymentsEnabled ? "Paiements PayPal désactivés" : "Paiements PayPal réactivés");
      await loadLinks();
    } catch (requestError) {
      toast.error(getApiErrorMessage(requestError, "Changement statut PayPal impossible"));
    }
  };

  return (
    <section className="surface p-4" data-testid="paypal-settings-section">
      <div className="flex flex-wrap items-start justify-between gap-3" data-testid="paypal-settings-header">
        <div>
          <h2 className="font-[Outfit] text-xl text-white" data-testid="paypal-settings-title">Paiement PayPal</h2>
          <p className="mt-2 text-sm text-slate-400" data-testid="paypal-settings-description">Associez chaque montant exact à son lien Hosted Button PayPal. Aucun SDK ni API PayPal n’est utilisé.</p>
        </div>
        <div className="flex flex-wrap gap-2"><button type="button" className={paymentsEnabled ? "btn-secondary" : "btn-primary"} onClick={togglePaymentsEnabled} data-testid="paypal-payments-enabled-toggle-button">{paymentsEnabled ? "Désactiver PayPal" : "Réactiver PayPal"}</button><button type="button" className="btn-secondary" onClick={loadLinks} disabled={loading} data-testid="paypal-settings-refresh-button"><RefreshCw size={16} /> Actualiser</button></div>
      </div>

      {!paymentsEnabled && <p className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="paypal-temporarily-unavailable-alert">Paiement PayPal temporairement indisponible</p>}

      <form className="mt-5 grid gap-3 lg:grid-cols-[140px_110px_minmax(0,1fr)_150px_auto]" onSubmit={submit} data-testid="paypal-link-form">
        <label className="field-label" htmlFor="paypal-link-amount">Montant<input id="paypal-link-amount" className="field mt-2" type="number" min="0" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} data-testid="paypal-link-amount-input" required /></label>
        <label className="field-label" htmlFor="paypal-link-currency">Devise<input id="paypal-link-currency" className="field mt-2 uppercase" maxLength={3} value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })} data-testid="paypal-link-currency-input" required /></label>
        <label className="field-label" htmlFor="paypal-link-url">URL Hosted PayPal<input id="paypal-link-url" className="field mt-2" type="url" placeholder="https://www.paypal.com/..." value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} data-testid="paypal-link-url-input" required /></label>
        <label className="field-label" htmlFor="paypal-link-active">Statut<select id="paypal-link-active" className="field mt-2" value={form.active ? "active" : "inactive"} onChange={(event) => setForm({ ...form, active: event.target.value === "active" })} data-testid="paypal-link-active-select"><option value="active">Actif</option><option value="inactive">Inactif</option></select></label>
        <div className="flex items-end gap-2">
          <button type="submit" className="btn-primary w-full" disabled={saving} data-testid="paypal-link-submit-button"><Plus size={16} /> {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter"}</button>
          {editingId && <button type="button" className="btn-secondary" onClick={reset} data-testid="paypal-link-cancel-edit-button">Annuler</button>}
        </div>
      </form>

      {error && <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200" data-testid="paypal-settings-error">{error}</p>}

      <div className={unmatchedSummary.total > 0 ? "mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100" : "mt-4 rounded-md border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-100"} data-testid="paypal-unmatched-amounts-indicator">
        {unmatchedSummary.total > 0 ? <p data-testid="paypal-unmatched-amounts-message"><strong>{unmatchedSummary.total} montant{unmatchedSummary.total > 1 ? "s" : ""} utilisé{unmatchedSummary.total > 1 ? "s" : ""} sans lien PayPal actif :</strong> {unmatchedLabel}</p> : <p data-testid="paypal-unmatched-amounts-ok">Tous les montants utilisés dans les abonnements ont un lien PayPal actif.</p>}
      </div>

      <div className="mt-5 overflow-x-auto" data-testid="paypal-links-table-wrap">
        <table className="data-table" data-testid="paypal-links-table">
          <thead><tr><th>Montant</th><th>Statut</th><th>URL</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4} data-testid="paypal-links-loading">Chargement…</td></tr> : links.length === 0 ? <tr><td colSpan={4}><div className="empty-state" data-testid="paypal-links-empty"><p className="font-[Outfit] text-lg text-white">Aucun lien PayPal configuré</p><p className="mt-1 text-sm text-slate-400">Ajoutez un montant exact pour afficher le bouton côté client.</p></div></td></tr> : links.map((link) => {
              const key = `${Number(link.amount).toFixed(2)} ${link.currency}`;
              return (
                <tr key={link.id} data-testid={`paypal-link-row-${link.id}`}>
                  <td data-testid={`paypal-link-amount-${link.id}`}>{moneyValue(link.amount, link.currency)}{link.active && activeByAmount[key] === link.id && <p className="text-xs text-slate-500" data-testid={`paypal-link-active-unique-${link.id}`}>Configuration active pour {key}</p>}</td>
                  <td><button type="button" className={link.active ? "badge badge-green" : "badge badge-neutral"} onClick={() => toggleLink(link)} data-testid={`paypal-link-toggle-${link.id}`}>{link.active ? "Actif" : "Inactif"}</button></td>
                  <td className="max-w-[420px] truncate" title={paymentsEnabled ? link.url : "Lien masqué temporairement"} data-testid={`paypal-link-url-${link.id}`}>{paymentsEnabled ? <a href={link.url} target="_blank" rel="noreferrer" data-testid={`paypal-link-open-${link.id}`}>{link.url} <ExternalLink size={13} className="inline" /></a> : <span className="text-amber-100" data-testid={`paypal-link-disabled-${link.id}`}>Paiement PayPal temporairement indisponible</span>}</td>
                  <td><div className="flex flex-wrap gap-2"><button type="button" className="btn-secondary" onClick={() => editLink(link)} data-testid={`paypal-link-edit-${link.id}`}>Modifier</button><button type="button" className="btn-secondary" onClick={() => deleteLink(link)} data-testid={`paypal-link-delete-${link.id}`}><Trash2 size={15} /> Supprimer</button></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}